import type { InvitationView, OrgMember, OrgProject, Project } from '@p2b/contract';
import type { ReactNode } from 'react';
import { formatDay, useI18n, type Lang } from '../i18n.js';

// Parts of the organization page that need no API client, so tests can render and check them.

/** "{n} days ago" with its values filled in. */
export function fill(s: string, vars: Record<string, string | number>): string {
  return s.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

/**
 * The projects an invitation to this organization may carry: the organization's own projects that the inviter
 * manages. A superadmin manages every project, so without the organization filter Heimar's invite form offered
 * Reitir's projects (found 2026-11-01). The server refuses them too (`project_not_in_org`).
 */
export function invitableProjects<P extends Project>(projects: readonly P[], org: string): P[] {
  return projects.filter((p) => p.org === org && (p.capabilities ?? []).includes('manage'));
}

/** A project's state on the register, derived from its publications (owner 2026-11-01). */
export type ProjectState = 'delivered' | 'inProgress';
export const projectState = (p: OrgProject): ProjectState =>
  p.latestPublication ? 'delivered' : 'inProgress';

export function filterProjects(
  list: readonly OrgProject[],
  q: string,
  state: ProjectState | 'all',
): OrgProject[] {
  const needle = q.trim().toLocaleLowerCase('is');
  return list.filter(
    (p) =>
      (state === 'all' || projectState(p) === state) &&
      (!needle ||
        [p.name, p.address, p.municipality, p.landNumber, p.slug].some((v) =>
          v?.toLocaleLowerCase('is').includes(needle),
        )),
  );
}

/** One row of the members table: a member, or an invitation still waiting. */
export type PersonRow =
  | { kind: 'member'; key: string; member: OrgMember }
  | { kind: 'invitation'; key: string; invitation: InvitationView };

export type PeopleFilter = 'all' | 'active' | 'invited';

/** Members first (by name), then invitations that can still be acted on (pending or expired). */
export function peopleRows(
  members: readonly OrgMember[],
  invitations: readonly InvitationView[],
): PersonRow[] {
  const byName = [...members].sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email, 'is'));
  return [
    ...byName.map((m) => ({ kind: 'member' as const, key: m.userId, member: m })),
    ...invitations
      .filter((i) => i.status === 'pending' || i.status === 'expired')
      .map((i) => ({ kind: 'invitation' as const, key: i.id, invitation: i })),
  ];
}

export function filterPeople(rows: readonly PersonRow[], q: string, filter: PeopleFilter): PersonRow[] {
  const needle = q.trim().toLocaleLowerCase('is');
  return rows.filter((r) => {
    if (filter === 'active' && r.kind !== 'member') return false;
    if (filter === 'invited' && r.kind !== 'invitation') return false;
    if (!needle) return true;
    const hay = r.kind === 'member' ? [r.member.name, r.member.email] : [r.invitation.email];
    return hay.some((v) => v.toLocaleLowerCase('is').includes(needle));
  });
}

/** Addresses typed into the invite dialog: split on commas, semicolons, spaces and lines; unique; lower case. */
export function parseEmails(text: string): { valid: string[]; invalid: string[] } {
  const parts = text
    .split(/[\s,;]+/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  const valid: string[] = [];
  const invalid: string[] = [];
  for (const p of parts) {
    const list = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p) ? valid : invalid;
    if (!list.includes(p)) list.push(p);
  }
  return { valid, invalid };
}

/** "í dag", "í gær", "fyrir 3 dögum", then the date. */
export function relativeDay(
  iso: string,
  lang: Lang,
  t: (k: 'today' | 'yesterday' | 'daysAgo') => string,
  now = new Date(),
): string {
  const d = new Date(iso);
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((day(now) - day(d)) / 86_400_000);
  if (days <= 0) return t('today');
  if (days === 1) return t('yesterday');
  if (days < 7) return fill(t('daysAgo'), { n: days });
  return formatDay(iso, lang);
}

/** A member's projects in a cell: "Öll" when they reach every one, else the first few and how many more. */
export function projectsCell(names: readonly string[], total: number, allLabel: string, shown = 2): string {
  if (names.length === 0) return '—';
  if (total > 1 && names.length === total) return allLabel;
  if (names.length <= shown + 1) return names.join(', ');
  return `${names.slice(0, shown).join(', ')} +${names.length - shown}`;
}

/** "010101-0190" from the stored ten digits. */
export function formatKennitala(kt: string): string {
  return /^\d{10}$/.test(kt) ? `${kt.slice(0, 6)}-${kt.slice(6)}` : kt;
}

/** Initials for the avatar circle. */
export function initials(name: string | null | undefined, email: string): string {
  const words = (name || '').trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase();
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return email.slice(0, 2).toUpperCase();
}

/** A line and, when the reader may act, the one action that fills the empty space. */
export function EmptyState({ text, action }: { text: string; action?: ReactNode }) {
  return (
    <div className="empty-state">
      <p className="muted">{text}</p>
      {action}
    </div>
  );
}

/** The members tab with nobody in it: say so, and offer the invitation to those who may send one. */
export function PeopleEmpty({ onInvite }: { onInvite?: () => void }) {
  const { t } = useI18n();
  return (
    <EmptyState
      text={t('noMembers')}
      action={
        onInvite && (
          <button className="primary" onClick={onInvite}>
            {t('inviteMember')}
          </button>
        )
      }
    />
  );
}
